"use client";
import React, { useState, useEffect } from "react";
import { Typography, Container, Box, Paper, Link } from "@mui/material";
import { Plus_Jakarta_Sans, Inter } from "next/font/google";
import { useTranslation } from "react-i18next"; // ← ADD

const PlusJakartaSans = Plus_Jakarta_Sans({
  subsets: ["latin"],
  weight: ["300", "400", "500", "600", "700"],
});

const inter = Inter({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700"],
});

const TableOfContents = ({ isArabic }) => {
  // ← RECEIVE isArabic
  const { t } = useTranslation(); // ← ADD
  const [activeId, setActiveId] = useState("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { threshold: 0.5 },
    );
    document
      .querySelectorAll("h1, h2, h3, h4")
      .forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Paper
      elevation={3}
      sx={{
        position: "fixed",
        right: isArabic ? "auto" : { xs: "5%", sm: "9.5%" }, // ← RTL flip
        left: isArabic ? { xs: "5%", sm: "9.5%" } : "auto", // ← RTL flip
        top: { xs: "50%", sm: "66.6%" },
        transform: "translateY(-50%)",
        display: { xs: "none", sm: "none", md: "none", xl: "none" },
        width: "280px",
        p: 2,
        maxHeight: "calc(80vh - 20%)",
        overflowY: "auto",
        scrollBehavior: "smooth",
        textAlign: isArabic ? "right" : "left", // ← ADD
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        sx={{ fontFamily: PlusJakartaSans.style.fontFamily, fontWeight: "700" }}
      >
        {t("articleFiveContent.toc.title")} {/* ← REPLACE */}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {[
          { id: "overview", key: "overview", indent: 0 },
          { id: "what-is-bad-hire", key: "whatIsBadHire", indent: 0 },
          { id: "devastating-cost", key: "devastatingCost", indent: 0 },
          { id: "lower-productivity", key: "lowerProductivity", indent: 1 },
          { id: "team-morale", key: "teamMorale", indent: 1 },
          { id: "lost-clients", key: "lostClients", indent: 1 },
          { id: "damaged-reputation", key: "damagedReputation", indent: 1 },
          { id: "decreased-teamwork", key: "decreasedTeamwork", indent: 1 },
          { id: "lost-time", key: "lostTime", indent: 1 },
          { id: "recruitment-costs", key: "recruitmentCosts", indent: 1 },
          {
            id: "prevention-strategies",
            key: "preventionStrategies",
            indent: 0,
          },
          { id: "assess-culture", key: "assessCulture", indent: 1 },
          { id: "hiring-strategy", key: "hiringStrategy", indent: 1 },
          { id: "skills-tests", key: "skillsTests", indent: 1 },
          { id: "examine-roles", key: "examineRoles", indent: 1 },
          { id: "hiring-tools", key: "hiringTools", indent: 1 },
          { id: "conclusion", key: "conclusion", indent: 0 },
        ].map(({ id, key, indent }) => (
          <Typography
            key={id}
            onClick={() => scrollToSection(id)}
            sx={{
              cursor: "pointer",
              pl: isArabic ? 0 : indent * 2, // ← RTL flip
              pr: isArabic ? indent * 2 : 0, // ← RTL flip
              color: activeId === id ? "primary.main" : "#4B5963",
              fontFamily: inter.style.fontFamily,
              fontSize: "14px",
              "&:hover": { color: "primary.main" },
            }}
          >
            {t(`articleFiveContent.toc.${key}`)} {/* ← REPLACE */}
          </Typography>
        ))}
      </Box>
    </Paper>
  );
};

const ArticleFiveContent = () => {
  const { t, i18n } = useTranslation(); // ← ADD
  const isArabic = i18n.language === "ar"; // ← ADD

  const bodyStyle = {
    fontFamily: inter.style.fontFamily,
    fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
    color: "#4B5963",
    fontWeight: "400",
  };

  return (
    <Container maxWidth="xl" sx={{ direction: isArabic ? "rtl" : "ltr" }}>
      {" "}
      {/* ← ADD */}
      <TableOfContents isArabic={isArabic} /> {/* ← PASS isArabic */}
      <Paper elevation={3} sx={{ my: 4, borer: "none", boxShadow: "none" }}>
        {/* Overview */}
        <Typography
          id="overview"
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontFamily: PlusJakartaSans.style.fontFamily,
            fontWeight: "700",
            fontSize: { xs: "24px", sm: "28px", md: "34px" },
          }}
        >
          {t("articleFiveContent.overview.heading")}
        </Typography>
        <Typography sx={{ mb: ".75%", ...bodyStyle }}>
          {t("articleFiveContent.overview.p1")}
        </Typography>
        <Typography sx={{ mb: ".75%", ...bodyStyle }}>
          {t("articleFiveContent.overview.p2")}
        </Typography>
        <Typography sx={{ mb: ".75%", ...bodyStyle }}>
          {t("articleFiveContent.overview.p3")}
        </Typography>
        <Typography sx={{ mb: ".75%", ...bodyStyle }}>
          {t("articleFiveContent.overview.p4")}
        </Typography>
        <Typography sx={{ ...bodyStyle }}>
          {t("articleFiveContent.overview.p5")}
        </Typography>

        {/* What is a bad hire */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="what-is-bad-hire"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              mt: 2,
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "700",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFiveContent.whatIsBadHire.heading")}
          </Typography>
          <Typography sx={{ mt: "1%", mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.whatIsBadHire.p1")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.whatIsBadHire.p2Start")}
            <Link
              href="https://www.zippia.com/advice/average-cost-of-a-bad-hire/"
              target="_blank"
              sx={{ color: "#9048FB", textDecoration: "none" }}
            >
              {t("articleFiveContent.whatIsBadHire.p2Link")}
            </Link>
            {t("articleFiveContent.whatIsBadHire.p2End")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.whatIsBadHire.p3Start")}
            <Link
              href="https://influx.com/blog/hidden-costs-of-hiring-2023-stats#:~:text=The%20cost%20of%20a%20bad%20hire,-It%20goes%20without&text=34%25%20of%20CFOs%20said%20that,of%20%2414%2C900%20in%20wasted%20money."
              target="_blank"
              sx={{ color: "#9048FB", textDecoration: "none" }}
            >
              {t("articleFiveContent.whatIsBadHire.p3Link")}
            </Link>
            {t("articleFiveContent.whatIsBadHire.p3End")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.whatIsBadHire.p4")}
          </Typography>
          <Typography sx={{ ...bodyStyle }}>
            {t("articleFiveContent.whatIsBadHire.p5")}
          </Typography>
        </Box>

        {/* Devastating Cost */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="devastating-cost"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              mt: 4,
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "700",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFiveContent.devastatingCost.heading")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.devastatingCost.p1")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.devastatingCost.p2")}
          </Typography>
          <Typography sx={{ ...bodyStyle }}>
            {t("articleFiveContent.devastatingCost.p3")}
          </Typography>
        </Box>

        {/* Lower Productivity */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="lower-productivity"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              mt: 4,
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "700",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFiveContent.lowerProductivity.heading")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.lowerProductivity.p1")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.lowerProductivity.p2")}
          </Typography>
          <Typography sx={{ ...bodyStyle }}>
            {t("articleFiveContent.lowerProductivity.p3")}
          </Typography>
        </Box>

        {/* Team Morale */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="team-morale"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              mt: 4,
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "700",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFiveContent.teamMorale.heading")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.teamMorale.p1")}
          </Typography>
          <Typography sx={{ ...bodyStyle }}>
            {t("articleFiveContent.teamMorale.p2Start")}
            <Link
              href="https://www.zippia.com/advice/average-cost-of-a-bad-hire/"
              target="_blank"
              sx={{ color: "#9048FB", textDecoration: "none" }}
            >
              {t("articleFiveContent.teamMorale.p2Link")}
            </Link>
            {t("articleFiveContent.teamMorale.p2End")}
          </Typography>
        </Box>

        {/* Lost Clients */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="lost-clients"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              mt: 4,
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "700",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFiveContent.lostClients.heading")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.lostClients.p1")}
          </Typography>
          <Typography sx={{ ...bodyStyle }}>
            {t("articleFiveContent.lostClients.p2")}
          </Typography>
        </Box>

        {/* Damaged Reputation */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="damaged-reputation"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              mt: 4,
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "700",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFiveContent.damagedReputation.heading")}
          </Typography>
          <Typography sx={{ ...bodyStyle }}>
            {t("articleFiveContent.damagedReputation.p1")}
          </Typography>
        </Box>

        {/* Decreased Teamwork */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="decreased-teamwork"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              mt: 4,
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "700",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFiveContent.decreasedTeamwork.heading")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.decreasedTeamwork.p1")}
          </Typography>
          <Typography sx={{ ...bodyStyle }}>
            {t("articleFiveContent.decreasedTeamwork.p2")}
          </Typography>
        </Box>

        {/* Lost Time */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="lost-time"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              mt: 4,
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "700",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFiveContent.lostTime.heading")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.lostTime.p1")}
          </Typography>
          <Typography sx={{ ...bodyStyle }}>
            {t("articleFiveContent.lostTime.p2")}
          </Typography>
        </Box>

        {/* Recruitment Costs */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="recruitment-costs"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              mt: 4,
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "700",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFiveContent.recruitmentCosts.heading")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.recruitmentCosts.p1")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.recruitmentCosts.p2Start")}
            <Link
              href="https://www.zippia.com/advice/average-cost-of-a-bad-hire/"
              target="_blank"
              sx={{ color: "#9048FB", textDecoration: "none" }}
            >
              {t("articleFiveContent.recruitmentCosts.p2Link")}
            </Link>
            {t("articleFiveContent.recruitmentCosts.p2End")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.recruitmentCosts.p3")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.recruitmentCosts.p4")}
          </Typography>
          <Typography sx={{ ...bodyStyle }}>
            {t("articleFiveContent.recruitmentCosts.p5")}
          </Typography>
        </Box>

        {/* Prevention Strategies */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="prevention-strategies"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              mt: 4,
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "700",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFiveContent.preventionStrategies.heading")}
          </Typography>

          {/* Assess Culture */}
          <Typography
            id="assess-culture"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              mt: 1,
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "600",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFiveContent.assessCulture.heading")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.assessCulture.p1")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.assessCulture.p2")}
          </Typography>
          <Typography sx={{ ...bodyStyle }}>
            {t("articleFiveContent.assessCulture.p3")}
          </Typography>
        </Box>

        {/* Hiring Strategy */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="hiring-strategy"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              mt: 1,
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "600",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFiveContent.hiringStrategy.heading")}
          </Typography>
          <Typography sx={{ ...bodyStyle }}>
            {t("articleFiveContent.hiringStrategy.p1")}
          </Typography>
        </Box>

        {/* Skills Tests */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="skills-tests"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              mt: 1,
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "600",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFiveContent.skillsTests.heading")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.skillsTests.p1")}
          </Typography>
          <Typography sx={{ ...bodyStyle }}>
            {t("articleFiveContent.skillsTests.p2")}
          </Typography>
        </Box>

        {/* Examine Roles */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="examine-roles"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              mt: 1,
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "600",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFiveContent.examineRoles.heading")}
          </Typography>
          <Typography sx={{ ...bodyStyle }}>
            {t("articleFiveContent.examineRoles.p1")}
          </Typography>
        </Box>

        {/* Hiring Tools */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="hiring-tools"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              mt: 1,
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "600",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFiveContent.hiringTools.heading")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.hiringTools.p1")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.hiringTools.p2")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.hiringTools.p3")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.hiringTools.p4")}
          </Typography>
          <Typography sx={{ mb: ".75%", ...bodyStyle }}>
            {t("articleFiveContent.hiringTools.p5")}
          </Typography>
          <Typography sx={{ ...bodyStyle }}>
            {t("articleFiveContent.hiringTools.p6")}
          </Typography>
        </Box>

        {/* Conclusion */}
        <Typography
          id="conclusion"
          variant="h4"
          component="h2"
          gutterBottom
          sx={{
            mt: 4,
            fontFamily: PlusJakartaSans.style.fontFamily,
            fontWeight: "700",
            fontSize: { xs: "24px", sm: "28px", md: "34px" },
          }}
        >
          {t("articleFiveContent.conclusion.heading")}
        </Typography>
        <Typography sx={{ mb: ".75%", ...bodyStyle }}>
          {t("articleFiveContent.conclusion.p1")}
        </Typography>
        <Typography sx={{ mb: ".75%", ...bodyStyle }}>
          {t("articleFiveContent.conclusion.p2")}
        </Typography>
        <Typography sx={{ ...bodyStyle }}>
          {t("articleFiveContent.conclusion.p3")}
        </Typography>
      </Paper>
    </Container>
  );
};

export default ArticleFiveContent;
